import { useState } from "react";
import { X, Mail, HelpCircle, CheckCircle, Loader2, ChevronLeft } from "lucide-react";
import { useTurnstile } from "@/hooks/useTurnstile";
import { SECURITY_QUESTIONS } from "@/lib/security-questions";
import { safeFetch } from "@/lib/safeFetch";

const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

async function apiFetch(path: string, body: Record<string, unknown>) {
  const res = await safeFetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data;
}

type Tab = "email" | "questions";
type QStep = "lookup" | "answer" | "newpass" | "done";
type EStep = "form" | "sent";

interface Props {
  onClose: () => void;
}

export function ForgotPasswordModal({ onClose }: Props) {
  const [tab, setTab] = useState<Tab>("email");

  // Email flow
  const [eEmail, setEEmail] = useState("");
  const [eStep, setEStep] = useState<EStep>("form");
  const [eLoading, setELoading] = useState(false);
  const [eError, setEError] = useState("");
  const emailTurnstile = useTurnstile();

  // Questions flow
  const [qIdentifier, setQIdentifier] = useState("");
  const [qStep, setQStep] = useState<QStep>("lookup");
  const [qQuestions, setQQuestions] = useState<string[]>([]);
  const [qAnswers, setQAnswers] = useState(["", "", ""]);
  const [qResetToken, setQResetToken] = useState("");
  const [qNewPass, setQNewPass] = useState("");
  const [qConfirmPass, setQConfirmPass] = useState("");
  const [qLoading, setQLoading] = useState(false);
  const [qError, setQError] = useState("");
  const [qCaptchaRequired, setQCaptchaRequired] = useState(false);
  const qTurnstile = useTurnstile();
  // Separate Turnstile instance for the answer-verification step
  const answerTurnstile = useTurnstile();

  const inputCls = "w-full bg-background border border-primary/30 text-primary font-mono text-sm px-3 py-2.5 focus:outline-none focus:border-primary placeholder:text-primary/20";
  const labelCls = "block text-primary/60 font-mono text-[10px] tracking-widest mb-1.5 uppercase";
  const btnCls = "w-full bg-primary/10 border border-primary/40 text-primary font-mono text-xs tracking-widest py-2.5 hover:bg-primary/20 hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all";
  const tabCls = (active: boolean) =>
    `flex-1 py-2 font-mono text-xs tracking-widest border-b-2 transition-all ${
      active
        ? "border-primary text-primary"
        : "border-primary/15 text-primary/30 hover:text-primary/60"
    }`;

  // ── Email flow ──────────────────────────────────────────────────
  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEError("");
    setELoading(true);
    try {
      if (emailTurnstile.scriptBlocked) {
        setEError("Verification failed to load — disable ad blocker and refresh");
        return;
      }
      const tsToken = null;
      await apiFetch("/api/auth/forgot-password/email", {
        email: eEmail,
        turnstileToken: tsToken,
      });
      setEStep("sent");
    } catch (err) {
      emailTurnstile.reset();
      setEError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setELoading(false);
    }
  }

  // ── Security Questions flow ──────────────────────────────────────
  async function handleLookup(e: React.FormEvent) {
    e.preventDefault();
    setQError("");
    setQLoading(true);
    try {
      if (qTurnstile.scriptBlocked) {
        setQError("Verification failed to load — disable ad blocker and refresh");
        return;
      }
      const tsToken = null;
      const res = await safeFetch(`${BASE_URL}/api/auth/security-questions/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: qIdentifier.trim().toLowerCase(),
          turnstileToken: tsToken,
        }),
      });
      const data = await res.json() as { questions?: string[] | null; error?: string };
      if (!res.ok) {
        qTurnstile.reset();
        setQError(data.error ?? "Request failed");
        setQLoading(false);
        return;
      }
      if (!data.questions) {
        qTurnstile.reset();
        setQError("No security questions found for that account. Try the email method instead.");
        setQLoading(false);
        return;
      }
      setQQuestions(data.questions);
      setQAnswers(["", "", ""]);
      setQStep("answer");
    } catch (err) {
      console.warn("[ForgotPasswordModal] Security question fetch failed:", err);
      qTurnstile.reset();
      setQError(err instanceof Error ? err.message : "Could not look up account. Please try again.");
    } finally {
      setQLoading(false);
    }
  }

  async function handleAnswersSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (qAnswers.some(a => !a.trim())) {
      setQError("Please answer all 3 questions");
      return;
    }
    setQError("");
    setQLoading(true);
    try {
      if (qCaptchaRequired && answerTurnstile.scriptBlocked) {
        setQError("Verification failed to load — disable ad blocker and refresh");
        return;
      }
      const tsToken = qCaptchaRequired ? await answerTurnstile.getToken() : null;
      if (qCaptchaRequired && SITE_KEY && !tsToken) {
        setQError("Please complete verification");
        return;
      }
      const res = await safeFetch(`${BASE_URL}/api/auth/verify-security-answers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: qIdentifier.trim().toLowerCase(),
          answers: qAnswers.map(a => a.trim()),
          turnstileToken: tsToken,
        }),
      });
      const data = await res.json() as { resetToken?: string; error?: string; captchaRequired?: boolean };
      if (!res.ok) {
        if (data.captchaRequired) {
          setQCaptchaRequired(true);
          answerTurnstile.reset();
        }
        setQError(data.error ?? "Verification failed");
        return;
      }
      setQResetToken(data.resetToken!);
      setQCaptchaRequired(false);
      setQStep("newpass");
    } catch (err) {
      setQError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setQLoading(false);
    }
  }

  async function handleNewPassSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (qNewPass !== qConfirmPass) { setQError("Passwords do not match"); return; }
    setQError("");
    setQLoading(true);
    try {
      await apiFetch("/api/auth/reset-password", {
        questionResetToken: qResetToken,
        newPassword: qNewPass,
      });
      setQStep("done");
    } catch (err) {
      setQError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setQLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-sm border border-primary/30 bg-card shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-primary/15 sticky top-0 bg-card z-10">
          <span className="text-primary font-mono text-xs tracking-widest uppercase">Recover Access</span>
          <button onClick={onClose} className="text-primary/40 hover:text-primary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-primary/15">
          <button className={tabCls(tab === "email")} onClick={() => setTab("email")}>
            <Mail className="inline w-3 h-3 mr-1.5 -mt-0.5" />Via Email
          </button>
          <button className={tabCls(tab === "questions")} onClick={() => setTab("questions")}>
            <HelpCircle className="inline w-3 h-3 mr-1.5 -mt-0.5" />Security Questions
          </button>
        </div>

        <div className="p-5">

          {/* ── EMAIL TAB ── */}
          {tab === "email" && eStep === "form" && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <p className="text-primary/40 font-mono text-xs leading-relaxed">
                Enter your account email and we'll send you a password reset link.
              </p>
              <div>
                <label className={labelCls}>Email address</label>
                <input
                  type="email" required
                  value={eEmail} onChange={e => setEEmail(e.target.value)}
                  className={inputCls} placeholder="your@email.com"
                  autoFocus
                />
              </div>
              <div ref={emailTurnstile.containerRef} />
              {eError && <div className="text-red-400 font-mono text-xs border border-red-400/30 px-3 py-2 bg-red-400/5">{eError}</div>}
              {emailTurnstile.isLoading && (
                <p className="text-primary/50 font-mono text-xs text-center">Loading verification...</p>
              )}
              <button type="submit" disabled={eLoading || !eEmail || emailTurnstile.isLoading} className={btnCls}>
                {eLoading ? <><Loader2 className="inline w-3 h-3 mr-1.5 animate-spin" />Sending...</> : "SEND RESET LINK"}
              </button>
            </form>
          )}

          {tab === "email" && eStep === "sent" && (
            <div className="text-center py-4 space-y-3">
              <CheckCircle className="w-10 h-10 text-primary/70 mx-auto" />
              <p className="text-primary font-mono text-sm font-bold tracking-wider">CHECK YOUR INBOX</p>
              <p className="text-primary/40 font-mono text-xs leading-relaxed">
                If an account exists for <span className="text-primary/60">{eEmail}</span>, a reset link has been sent. Check your spam folder too.
              </p>
              <p className="text-primary/25 font-mono text-[10px]">The link expires in 1 hour.</p>
              <button onClick={onClose} className={`${btnCls} mt-4`}>CLOSE</button>
            </div>
          )}

          {/* ── SECURITY QUESTIONS TAB ── */}

          {/* Step 1 — enter email + captcha */}
          {tab === "questions" && qStep === "lookup" && (
            <form onSubmit={handleLookup} className="space-y-4">
              <p className="text-primary/40 font-mono text-xs leading-relaxed">
                Enter your email. We'll present the security questions you set up so you can verify your identity.
              </p>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  type="text" required
                  value={qIdentifier} onChange={e => setQIdentifier(e.target.value)}
                  className={inputCls} placeholder="your@email.com"
                  autoFocus autoComplete="username"
                />
              </div>
              <div ref={qTurnstile.containerRef} />
              {qError && <div className="text-red-400 font-mono text-xs border border-red-400/30 px-3 py-2 bg-red-400/5">{qError}</div>}
              {qTurnstile.isLoading && (
                <p className="text-primary/50 font-mono text-xs text-center">Loading verification...</p>
              )}
              <button type="submit" disabled={qLoading || !qIdentifier.trim() || qTurnstile.isLoading} className={btnCls}>
                {qLoading ? <><Loader2 className="inline w-3 h-3 mr-1.5 animate-spin" />Checking...</> : "CONTINUE"}
              </button>
            </form>
          )}

          {/* Step 2 — answer their 3 security questions */}
          {tab === "questions" && qStep === "answer" && (
            <form onSubmit={handleAnswersSubmit} className="space-y-4">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setQStep("lookup"); setQError(""); qTurnstile.reset(); answerTurnstile.reset(); setQCaptchaRequired(false); }}
                  className="text-primary/30 hover:text-primary/70 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-primary/40 font-mono text-xs">
                  Answer your security questions for <span className="text-primary/60">{qIdentifier}</span>
                </span>
              </div>
              {qQuestions.map((q, i) => (
                <div key={i} className="space-y-1.5">
                  <label className={labelCls}>Question {i + 1}</label>
                  <p className="text-primary/70 font-mono text-xs leading-relaxed border-l-2 border-primary/20 pl-2">{q}</p>
                  <input
                    type="text" required
                    value={qAnswers[i]}
                    onChange={e => { const a = [...qAnswers]; a[i] = e.target.value; setQAnswers(a); }}
                    className={inputCls} placeholder="Your answer"
                    autoFocus={i === 0}
                  />
                </div>
              ))}
              {/* Turnstile widget — mounts after 2 failed attempts */}
              {qCaptchaRequired && <div ref={answerTurnstile.containerRef} />}
              {qError && <div className="text-red-400 font-mono text-xs border border-red-400/30 px-3 py-2 bg-red-400/5">{qError}</div>}
              <p className="text-primary/20 font-mono text-[10px]">Answers are case-insensitive.</p>
              {qCaptchaRequired && answerTurnstile.isLoading && (
                <p className="text-primary/50 font-mono text-xs text-center">Loading verification...</p>
              )}
              <button type="submit" disabled={qLoading || qAnswers.some(a => !a.trim()) || (qCaptchaRequired && answerTurnstile.isLoading)} className={btnCls}>
                {qLoading ? <><Loader2 className="inline w-3 h-3 mr-1.5 animate-spin" />Verifying...</> : "VERIFY IDENTITY"}
              </button>
            </form>
          )}

          {/* Step 3 — set new password */}
          {tab === "questions" && qStep === "newpass" && (
            <form onSubmit={handleNewPassSubmit} className="space-y-4">
              <p className="text-primary/40 font-mono text-xs leading-relaxed">
                Identity verified. Choose a new password.
              </p>
              <div>
                <label className={labelCls}>New Password</label>
                <input
                  type="password" required
                  value={qNewPass} onChange={e => setQNewPass(e.target.value)}
                  className={`${inputCls} tracking-widest`} placeholder="••••••••"
                  autoFocus autoComplete="new-password"
                />
              </div>
              <div>
                <label className={labelCls}>Confirm Password</label>
                <input
                  type="password" required
                  value={qConfirmPass} onChange={e => setQConfirmPass(e.target.value)}
                  className={`${inputCls} tracking-widest`} placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              <p className="text-primary/25 font-mono text-[10px] leading-relaxed">
                Min 8 chars • Upper & lowercase • Special character • No long runs
              </p>
              {qError && <div className="text-red-400 font-mono text-xs border border-red-400/30 px-3 py-2 bg-red-400/5">{qError}</div>}
              <button type="submit" disabled={qLoading || !qNewPass || !qConfirmPass} className={btnCls}>
                {qLoading ? <><Loader2 className="inline w-3 h-3 mr-1.5 animate-spin" />Saving...</> : "SET NEW PASSWORD"}
              </button>
            </form>
          )}

          {/* Step 4 — done */}
          {tab === "questions" && qStep === "done" && (
            <div className="text-center py-4 space-y-3">
              <CheckCircle className="w-10 h-10 text-primary/70 mx-auto" />
              <p className="text-primary font-mono text-sm font-bold tracking-wider">PASSWORD UPDATED</p>
              <p className="text-primary/40 font-mono text-xs">You can now log in with your new password.</p>
              <button onClick={onClose} className={`${btnCls} mt-4`}>BACK TO LOGIN</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
